import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery } from '@mui/material';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import { useBranch } from '../../../contextApi/BranchContext';
import { usePermissions } from '../../../contextApi/PermissionContext';
import { actions } from '../../../store/store';
import useFormDraft from '../../../tools/hooks/useFormDraft';
import { normalizeDateSystem, toIsoDay, today } from '../../../tools/calendars';
import MisDateField from '../misDateField';
import { DateSystemSelect, branchDocDefaults } from '../docSettings';
import { DraftCloseDialog, newDraftKey, MIS_DRAFT } from '../misDraftStrip';
import PlaceSelect, { usePackingPlaces } from './placeSelect';
import { usePalletTemplates, SavePalletTemplateDialog, PalletTemplateMenu } from './palletTemplates';
import {
  EMPTY_PALLET, FIELD, Section, PalletCard, PHONE_BTN, PHONE_FORM_SX, palletCodeKey, palletForSave, palletFromRecord, palletHasContent, rowHasData,
  withKey, usePalletEditor, usePalletCodes,
} from './palletEditor';
import PalletCodeAdder from './palletCodeAdder';

const NO_SCOPE = new Set();
const NO_OPTIONS = [];
const isLocked = (p) => Boolean(p.existing && p.existing.list && p.existing.list.status === 'final');
const totalsOf = (items) => items.reduce((acc, it) => ({ pcs: acc.pcs + (Number(it.pcs) || 0), sqm: acc.sqm + (Number(it.sqm) || 0) }), { pcs: 0, sqm: 0 });
const dayOf = (value) => (value ? String(value).slice(0, 10) : toIsoDay(today()));

// A pallet on a final packing list: it has gone out, so it stays in the form as it is.
function LockedPalletRow({ pallet, canRemove, onRemove, T, isXs }) {
  const { t } = useTranslation();
  const sum = totalsOf(pallet.items);
  return (
    <Box data-pallet-key={pallet._key} data-pallet-locked sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '12px', border: `1px solid ${T.BD}`, bgcolor: T.CTRL_BG }}>
      <LockOutlinedIcon sx={{ fontSize: 16, color: T.TEXT_SEC }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 700, color: T.TEXT_PRI }}>
          <span dir="ltr">{pallet.reference}</span>{pallet.palletId ? ` · ${pallet.palletId}` : ''}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: T.TEXT_SEC }}>{t('mis.lblLockedNote', { list: pallet.existing.list.docNumber })}</Typography>
      </Box>
      <Typography sx={{ fontSize: '0.72rem', color: T.TEXT_SEC, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {sum.pcs} {t('mis.plColPcs')} · {sum.sqm.toFixed(4)} {t('mis.plColSqm')}
      </Typography>
      {canRemove && (
        <Tooltip title={t('mis.lfRemoveFromForm')}>
          <IconButton size="small" onClick={() => onRemove(pallet._key)} aria-label={t('mis.lfRemoveFromForm')} sx={isXs ? PHONE_BTN : undefined}>
            <CloseIcon sx={{ fontSize: 16, color: T.TEXT_TER }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

// A LABEL FORM: a batch of labels, saved as one record. Its number, date, loading origin, final destination and the person responsible for the
// palleting are said once; its pallets are typed here or brought in by their code, and a packing list takes them from it later.
//   form  (optional)  the form to change, as GET /mis/label-forms/:id answers it (with its pallets)
//   draft (optional)  an unsent NEW form kept in this browser, to carry on with (an older label draft opens here too)
// onClose() when it is cancelled, onClose({ form }) after a save.
export default function LabelFormForm({ open, onClose, form = null, draft = null }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const { activeBranchId, activeBranch } = useBranch();
  const { can } = usePermissions();

  const isEdit = Boolean(form);
  const branchId = isEdit ? form.branchId : activeBranchId;
  const canManage = can('mis:pallet:create') || can('mis:pallet:edit') || can('mis:packingList:create') || can('mis:packingList:edit');
  const canAddPallets = can('mis:pallet:create') || can('mis:packingList:create');
  const canCreateVariety = can('inventory:subproduct:create');
  const { places, add: addPlace, remove: removePlace } = usePackingPlaces(branchId, open);
  const { templates, save: saveTemplate, remove: removeTemplate } = usePalletTemplates(branchId, open);

  const T = useMemo(() => ({
    BD:       isDark ? 'rgba(255,255,255,0.07)' : theme.palette.divider,
    BD2:      isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
    TEXT_PRI: isDark ? 'rgba(255,255,255,0.87)' : theme.palette.text.primary,
    TEXT_SEC: isDark ? 'rgba(255,255,255,0.45)' : theme.palette.text.secondary,
    TEXT_TER: isDark ? 'rgba(255,255,255,0.2)'  : 'rgba(0,0,0,0.3)',
    CTRL_BG:  isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
    ROW_ALT:  isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    PULSE:    isDark ? 'rgba(255,255,255,0.2)'  : 'rgba(0,0,0,0.14)',
  }), [isDark, theme.palette.divider, theme.palette.text.primary, theme.palette.text.secondary]);

  const {
    pallets, setPallets, palletsRef, specKey, setSpecKey, movedKey, announce, flip, addPallet, addFromTemplate, addExisting, onInsertKey, duplicatePallet,
    removePallet, movePalletBy, movePalletTo, setPalletField, addItem, enterRow, removeItem, setItemField, resetSqm, toggleSpec, addBuiltItem, searchForBuilder,
  } = usePalletEditor({ branchId, autoIds: false });

  const [issueDate, setIssueDate] = useState(() => toIsoDay(today()));
  const [dateSystem, setDateSystem] = useState('gregorian');
  const [originAddress, setOriginAddress] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [palletizedBy, setPalletizedBy] = useState('');
  const [notes, setNotes] = useState('');
  const [people, setPeople] = useState([]);
  const [templateFor, setTemplateFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hydratedAt, setHydratedAt] = useState(0);
  const [baseline, setBaseline] = useState(null);
  const [draftKey, setDraftKey] = useState('');
  const [fromDraft, setFromDraft] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  // the number: automatic (the branch's next, shown as the suggestion) until the person types their own
  const [docNumber, setDocNumber] = useState('');
  const [numberTouched, setNumberTouched] = useState(false);
  const [suggested, setSuggested] = useState(null);
  const [numberError, setNumberError] = useState('');
  const numberRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setError(''); setSpecKey(null); setConfirmClose(false); setFromDraft(false); setSaving(false); setNumberError('');
    if (form) {
      setPallets(form.pallets && form.pallets.length ? form.pallets.map((p) => withKey(palletFromRecord(p))) : [withKey({ ...EMPTY_PALLET })]);
      setIssueDate(dayOf(form.issueDate)); setDateSystem(normalizeDateSystem(form.dateSystem));
      setOriginAddress(form.originAddress || ''); setDestinationAddress(form.destinationAddress || '');
      setPalletizedBy(form.palletizedBy || ''); setNotes(form.notes || '');
      setDocNumber(String(form.docNumber || '')); setNumberTouched(false);
    } else if (draft && draft.snapshot) {
      const d = draft.snapshot;
      setDraftKey(draft.key); setFromDraft(true);
      setPallets(Array.isArray(d.pallets) && d.pallets.length ? d.pallets.map(withKey) : [withKey({ ...EMPTY_PALLET })]);
      setIssueDate(d.issueDate || toIsoDay(today())); setDateSystem(normalizeDateSystem(d.dateSystem || branchDocDefaults(activeBranch).dateSystem));
      setOriginAddress(d.originAddress || ''); setDestinationAddress(d.destinationAddress || '');
      setPalletizedBy(d.palletizedBy || ''); setNotes(d.notes || '');
      setDocNumber(d.docNumber || ''); setNumberTouched(Boolean(d.docNumber));
    } else {
      setDraftKey(newDraftKey('label', branchId));
      setPallets([withKey({ ...EMPTY_PALLET })]);
      setIssueDate(toIsoDay(today())); setDateSystem(branchDocDefaults(activeBranch).dateSystem);
      setOriginAddress(''); setDestinationAddress(''); setPalletizedBy(''); setNotes('');
      setDocNumber(''); setNumberTouched(false);
    }
    setBaseline(null);
    setHydratedAt(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form, draft]);

  // The names used before in this branch: the same few people do this every day.
  useEffect(() => {
    if (!open || !branchId) return undefined;
    let cancelled = false;
    authCtx.jwtInst({ method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/pallets/people`, params: { branchId } })
      .then((res) => { if (!cancelled) setPeople(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setPeople([]); });
    return () => { cancelled = true; };
  }, [open, branchId, authCtx, axiosGlobal]);

  // The number a new form would get now: only LOOKED at. Left alone, the server takes the next free one at Save.
  useEffect(() => {
    if (!open || isEdit || !branchId) return undefined;
    let cancelled = false;
    authCtx.jwtInst({ method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/label-forms/next-number`, params: { branchId } })
      .then((res) => { if (!cancelled) setSuggested(Number(res.data && res.data.next) || null); })
      .catch(() => { if (!cancelled) setSuggested(null); });
    return () => { cancelled = true; };
  }, [open, isEdit, branchId, hydratedAt, authCtx, axiosGlobal]);

  // A typed number another form of the branch carries is flagged as it is typed; the save checks again.
  useEffect(() => {
    if (!open || !(isEdit || numberTouched)) return undefined;
    const digits = String(docNumber || '');
    if (!digits || (isEdit && Number(digits) === Number(form.docNumber))) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      authCtx.jwtInst({
        method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/label-forms/number-available`,
        params: { branchId, number: digits, excludeId: isEdit ? form._id : undefined },
      }).then((res) => {
        if (!cancelled && res.data && res.data.valid && res.data.available === false) setNumberError(t('mis.lfNumberTaken', { number: Number(digits) }));
      }).catch(() => { /* the save will say it */ });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, numberTouched, docNumber, branchId]);

  const shownNumber = (isEdit || numberTouched) ? docNumber : (suggested == null ? '' : String(suggested));
  const typeNumber = (raw) => {
    const digits = String(raw).replace(/\D/g, '').slice(0, 8);
    setNumberError('');
    if (!isEdit && !digits) { setNumberTouched(false); setDocNumber(''); return; }
    setNumberTouched(true); setDocNumber(digits);
  };
  const resetNumber = () => { setNumberError(''); setNumberTouched(false); setDocNumber(''); };

  const { codeHints, takenFor, takenLine, takenError, setTakenCodes } = usePalletCodes({ open, pallets, palletsRef, refreshOn: hydratedAt });

  // Two pallets of one form cannot share a code: shown by their place on the form.
  const clashes = useMemo(() => {
    const byKey = new Map();
    pallets.forEach((p, i) => {
      const key = palletCodeKey(p.reference);
      if (key) byKey.set(key, [...(byKey.get(key) || []), i]);
    });
    const ref = {}, values = [];
    byKey.forEach((rows) => {
      if (rows.length < 2) return;
      values.push(String(pallets[rows[0]].reference).trim());
      rows.forEach((r) => { ref[r] = rows.filter((x) => x !== r).map((x) => x + 1).join(', '); });
    });
    return { ref, values };
  }, [pallets]);

  const totals = useMemo(() => totalsOf(pallets.flatMap((p) => p.items)), [pallets]);
  const counted = pallets.filter((p) => p.palletRecordId || palletHasContent(p)).length;

  // ── the unsent draft of a NEW form ───────────────────────────────────────
  const draftable = !isEdit;
  const typedNumber = numberTouched ? docNumber : '';
  const signature = JSON.stringify([pallets, palletizedBy, destinationAddress, originAddress, notes, issueDate, dateSystem, typedNumber]);
  useEffect(() => {
    if (open && hydratedAt) setBaseline(signature);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydratedAt]);
  const dirty = open && draftable && (fromDraft || (baseline !== null && signature !== baseline));
  const draftApi = useFormDraft({
    key: draftKey, kind: MIS_DRAFT.label.kind, enabled: open && draftable && Boolean(draftKey), dirty,
    snapshot: { pallets, palletizedBy, destinationAddress, originAddress, notes, issueDate, dateSystem, docNumber: typedNumber },
    meta: { branchId, title: [t('mis.lfLabelCount', { count: counted }), destinationAddress, palletizedBy].filter(Boolean).join(' · ') },
  });

  const handleClose = () => {
    if (saving) return;
    if (draftable && dirty) { setConfirmClose(true); return; }
    if (draftable) draftApi.clear();
    onClose();
  };

  const keepAsTemplate = async (name) => {
    const row = palletsRef.current.find((p) => p._key === templateFor);
    if (!row) return;
    await saveTemplate({
      name, productCode: row.productCode, processingType: row.processingType,
      items: row.items.filter(rowHasData).map(({ code, sort, lengthCm, widthCm, thicknessCm, pcs, sqm }) => ({ code, sort, lengthCm, widthCm, thicknessCm, pcs, sqm })),
    });
  };
  const askTemplate = useCallback((key) => setTemplateFor(key), []);

  const save = async () => {
    const filled = pallets.filter((p) => p.palletRecordId || palletHasContent(p));
    if (!filled.length) { setError(t('mis.lblNeedsContent')); return; }
    if (numberError) { setError(numberError); if (numberRef.current) numberRef.current.focus(); return; }
    if (clashes.values.length) { setError(clashes.values.map((code) => t('mis.lblDuplicateCode', { code })).join(' ')); return; }
    const takenHere = pallets.map((p) => takenFor(p.reference)).filter(Boolean);
    if (takenHere.length) { setError([...new Set(takenHere.map(takenError))].join(' ')); return; }

    setSaving(true); setError('');
    const data = {
      branchId, issueDate, dateSystem, originAddress: originAddress.trim(), destinationAddress: destinationAddress.trim(),
      palletizedBy: palletizedBy.trim(), notes: notes.trim(),
      // every pallet of the form, in its order: existing ones by their record, new ones as typed. The server keeps a pallet's own destination and
      // person, and writes nothing for one that did not change.
      pallets: filled.map((p) => (p.palletRecordId ? { _id: p.palletRecordId, ...palletForSave(p) } : palletForSave(p))),
      ...(isEdit ? { docNumber: docNumber || undefined } : (numberTouched && docNumber ? { docNumber } : {})),
    };
    try {
      const res = isEdit
        ? await authCtx.jwtInst({ method: 'put', url: `${axiosGlobal.defaultTargetApi}/mis/label-forms/${form._id}`, data })
        : await authCtx.jwtInst({ method: 'post', url: `${axiosGlobal.defaultTargetApi}/mis/label-forms`, data });
      if (draftable) await draftApi.clear();
      dispatch(actions.setShowSnackBar({ status: true, msg: t('mis.lfSaved', { number: res.data.docNumber }), type: 'success' }));
      onClose({ form: res.data });
    } catch (err) {
      const answer = (err && err.response && err.response.data) || {};
      if (answer.code === 'PALLET_CODE_TAKEN' && Array.isArray(answer.taken)) {
        setTakenCodes((cur) => ({ ...cur, ...Object.fromEntries(answer.taken.map((row) => [row.key, row])) }));
        setError(answer.taken.map(takenError).join(' '));
      } else if (answer.code === 'DOC_NUMBER_TAKEN') {
        setNumberError(t('mis.lfNumberTaken', { number: answer.docNumber })); setError(t('mis.lfNumberTaken', { number: answer.docNumber }));
        if (numberRef.current) numberRef.current.focus();
      } else if (answer.code === 'PALLET_IN_OTHER_FORM') {
        setError(t('mis.lfPalletInOtherForm', { code: answer.pallet, number: answer.docNumber }));
      } else if (answer.code === 'PALLET_LOCKED') {
        setError(t('mis.lblLockedError', { code: answer.pallet, list: answer.docNumber }));
      } else if (answer.code === 'DUPLICATE_PALLET') {
        setError(t('mis.lblDuplicateCode', { code: answer.value || '' }));
      } else if (answer.code === 'PALLET_NOT_FOUND') {
        setError(t('mis.lblNotFound', { codes: answer.pallet || '' }));
      } else if (answer.code === 'OUT_OF_SCOPE') {
        setError(t('mis.plOutOfScopeError', { codes: (answer.codes || []).join(', ') }));
      } else {
        setError(answer.message || t('mis.lfSaveFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const chip = { height: isXs ? 26 : 22, fontSize: isXs ? '0.74rem' : '0.65rem' };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={handleClose}
        PaperProps={{ onKeyDown: (e) => onInsertKey(e, saving), 'data-testid': 'lf-form', sx: { width: isXs ? '100vw' : 780, maxWidth: '100vw', ...(isXs ? PHONE_FORM_SX : {}) } }}>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 2, sm: 3 }, py: 2, borderBottom: `1px solid ${T.BD}`, flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: T.TEXT_PRI }}>
            {isEdit ? t('mis.lfEditTitle', { number: form.docNumber }) : t('mis.lfNewTitle')}
          </Typography>
          {draftable && draftApi.savedAt && (
            <Typography data-testid="draft-saved" sx={{ fontSize: '0.64rem', color: T.TEXT_TER, marginInlineStart: 'auto', marginInlineEnd: 1 }}>
              {t('mis.draftSaved', { time: new Date(draftApi.savedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}
            </Typography>
          )}
          <IconButton onClick={handleClose} size="small" aria-label={t('common.close')} sx={isXs ? PHONE_BTN : undefined}>
            <CloseIcon sx={{ fontSize: isXs ? 22 : 18 }} />
          </IconButton>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5, flex: 1, overflowY: 'auto' }}>
          {/* ── the form: its number, date, places and the person ── */}
          <Section T={T} label={t('mis.lfFormSection')}>
            <Box sx={{ display: 'grid', gridTemplateColumns: isXs ? '1fr' : '1fr 1fr', gap: 1.25, alignItems: 'start' }}>
              <TextField {...FIELD} label={t('mis.lfNumberField')} value={shownNumber} inputRef={numberRef}
                onChange={(e) => typeNumber(e.target.value)} placeholder={t('mis.plNumberAutomatic')}
                error={Boolean(numberError)}
                helperText={numberError || (isEdit ? t('mis.lfNumberEditHelp') : numberTouched ? t('mis.lfNumberCustomHelp') : t('mis.lfNumberAutoHelp'))}
                inputProps={{ inputMode: 'numeric', dir: 'ltr', maxLength: 8, 'data-testid': 'lf-number' }}
                InputProps={{
                  endAdornment: !isEdit && numberTouched ? (
                    <InputAdornment position="end">
                      <Tooltip title={t('mis.plNumberUseAuto')}>
                        <IconButton size="small" onClick={resetNumber} aria-label={t('mis.plNumberUseAuto')}><RestartAltIcon sx={{ fontSize: 16 }} /></IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{ '& input': { fontFamily: 'monospace', fontWeight: 700, letterSpacing: 0.5 } }} />
              <DateSystemSelect value={dateSystem} onChange={(v) => setDateSystem(v)} />
              <Box sx={{ gridColumn: '1 / -1' }}>
                <MisDateField label={t('mis.lfDateField')} system={dateSystem} value={issueDate} onChange={(iso) => setIssueDate(iso)} />
              </Box>
              <PlaceSelect kind="origin" label={t('mis.originAddressField')} value={originAddress} onChange={setOriginAddress}
                rows={places.origin} canManage={canManage} onAdd={addPlace} onRemove={removePlace} onError={setError} touch={isXs} />
              <PlaceSelect kind="destination" label={t('mis.destinationAddressField')} value={destinationAddress} onChange={setDestinationAddress}
                rows={places.destination} canManage={canManage} onAdd={addPlace} onRemove={removePlace} onError={setError} touch={isXs} />
              {/* freeSolo: the typed text IS the stored value. Often somebody with no account in the app. */}
              <Autocomplete freeSolo options={people} inputValue={palletizedBy} onInputChange={(_, v) => setPalletizedBy(v)}
                renderInput={(params) => (
                  <TextField {...params} {...FIELD} label={t('mis.lblPalletizedBy')} helperText={t('mis.lfPalletizedByHelp')}
                    inputProps={{ ...params.inputProps, 'data-testid': 'lbl-palletized-by', maxLength: 120 }} />
                )} />
              <TextField {...FIELD} label={t('mis.sectionNotes')} value={notes} multiline minRows={1} onChange={(e) => setNotes(e.target.value)}
                inputProps={{ 'data-testid': 'lf-notes', maxLength: 4000 }} />
            </Box>
            <Typography sx={{ fontSize: '0.68rem', color: T.TEXT_SEC, mt: 1 }}>{t('mis.lfPlacesHelp')}</Typography>
          </Section>

          <Section T={T} label={t('mis.lfLabelsSection')}>
            <Typography sx={{ fontSize: '0.68rem', color: T.TEXT_SEC, mb: 1 }}>{t('mis.plCodeAutoHelp')}</Typography>
            <Box role="status" aria-live="polite"
              sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
              {announce}
            </Box>

            <Box ref={flip.containerRef} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {pallets.map((row, pIdx) => (isLocked(row) ? (
                <LockedPalletRow key={row._key} pallet={row} canRemove={pallets.length > 1} onRemove={removePallet} T={T} isXs={isXs} />
              ) : (
                <PalletCard key={row._key || pIdx} pallet={row} index={pIdx}
                  isLast={pIdx === pallets.length - 1} canRemove={pallets.length > 1}
                  isMoved={Boolean(movedKey) && movedKey === row._key}
                  clashIdText="" clashRefText={clashes.ref[pIdx] || ''}
                  takenText={takenFor(row.reference) ? takenLine(takenFor(row.reference)) : ''}
                  codeHint={codeHints[row._key] || ''} idHint={t('mis.lblIdOptional')} canTemplate={canManage} onSaveTemplate={askTemplate}
                  isXs={isXs} T={T} type="free" scopePrefixes={NO_SCOPE} codeOptions={NO_OPTIONS}
                  specOpen={specKey === row._key} canCreateVariety={canCreateVariety} searchProducts={searchForBuilder}
                  onMoveBy={movePalletBy} onMoveTo={movePalletTo} onDuplicate={duplicatePallet} onRemove={removePallet}
                  onField={setPalletField} onAddItem={addItem} onEnterRow={enterRow} onRemoveItem={removeItem} onItemField={setItemField}
                  onResetSqm={resetSqm} onToggleSpec={toggleSpec} onUseBuilt={addBuiltItem} />
              )))}
            </Box>

            {canAddPallets && (
              <Box sx={{ display: 'flex', flexDirection: isXs ? 'column' : 'row', flexWrap: 'wrap', alignItems: isXs ? 'stretch' : 'center', gap: isXs ? 1 : 0.5, mt: 1.25 }}>
                <Button size="small" variant={isXs ? 'outlined' : 'text'} startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => addPallet()} data-testid="lbl-add-pallet"
                  sx={{ fontSize: '0.7rem', textTransform: 'none', ...(isXs ? { width: '100%', minHeight: 48, fontSize: '0.85rem', borderStyle: 'dashed' } : {}) }}>
                  {t('mis.lfAddLabel')}
                </Button>
                <PalletTemplateMenu templates={templates} canManage={canManage} onPick={addFromTemplate} onRemove={removeTemplate}
                  onError={setError} touch={isXs} T={T} />
              </Box>
            )}
            <PalletCodeAdder forForm formId={isEdit ? form._id : null} branchId={branchId} heldCodes={pallets.map((p) => p.reference)} onAdd={addExisting} touch={isXs} T={T} />
            {!isXs && <Typography sx={{ fontSize: '0.66rem', color: T.TEXT_TER, mt: 0.5 }}>{t('mis.lfKeysHint')}</Typography>}
          </Section>

          {error && <Alert severity="error" data-testid="lf-error" sx={{ mt: 1.5, fontSize: '0.75rem', py: 0.25 }}>{error}</Alert>}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, px: { xs: 2, sm: 3 }, pt: 2,
          pb: { xs: 'max(16px, env(safe-area-inset-bottom))', sm: 2 }, borderTop: `1px solid ${T.BD}`, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip size="small" label={t('mis.lfLabelCount', { count: counted })} sx={chip} />
            <Chip size="small" label={`${totals.pcs} ${t('mis.plColPcs')}`} sx={chip} />
            <Chip size="small" label={`${totals.sqm.toFixed(4)} ${t('mis.plColSqm')}`} sx={chip} />
          </Box>
          <Box sx={{ marginInlineStart: { sm: 'auto' }, display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
            <Button onClick={handleClose} disabled={saving} sx={{ textTransform: 'none', flex: { xs: 1, sm: 'none' }, minHeight: { xs: 48, sm: 0 } }}>{t('common.cancel')}</Button>
            <Button variant="contained" onClick={save} disabled={saving} data-testid="lf-save"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ textTransform: 'none', flex: { xs: 2, sm: 'none' }, minHeight: { xs: 48, sm: 0 } }}>
              {t('common.save')}
            </Button>
          </Box>
        </Box>
      </Drawer>

      <SavePalletTemplateDialog open={Boolean(templateFor)} pallet={templateFor ? pallets.find((p) => p._key === templateFor) : null} T={T}
        onSave={keepAsTemplate} onClose={() => setTemplateFor(null)} />

      {draftable && (
        <DraftCloseDialog open={confirmClose} onCancel={() => setConfirmClose(false)}
          onKeep={async () => { await draftApi.flush(); setConfirmClose(false); onClose(); }}
          onDiscard={async () => { await draftApi.clear(); setConfirmClose(false); onClose(); }} />
      )}
    </>
  );
}
